import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'
import { WRAPPED_SLIDES } from '../../config/slides'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

const ButtonContainer = styled(InteractiveZone)`
  margin-top: ${rem(8)};
`

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'prediction')
const SLIDE_ID = 'prediction' as const

function PredictionSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  // Pre-fetched prediction data from useWrappedData hook - avoids flickering
  const predictionData = useWrappedStorySelector(state => state.predictionData)

  // Use the video background with fallback hook for when no content image is available
  const { background: videoBackground, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const activeBooking = data?.activeBooking

  const imageId = activeBooking?.imageId ?? predictionData?.imageId

  // Use content-specific image if available, otherwise use video background (with fallback)
  const background = useMemo(() => {
    if (imageId) {
      return <WrappedImageBackground imageId={imageId} kenBurnsVariant="zoom" isActive={isCurrentSlide && !isPaused} />
    }
    return videoBackground
  }, [imageId, isCurrentSlide, isPaused, videoBackground])

  const destinationName = predictionData?.destinationName
  const searchUrl = predictionData?.searchUrl ?? ''
  const offerCount = predictionData?.offerCount ?? 0
  const showSavings = (predictionData?.maxSavingsPercent ?? 0) > 0

  // Extract strings as constants before return
  const captionText = intl.formatMessage({
    id: 'LgZLSDUWYAMzskBnR7hr',
    defaultMessage: 'WHAT’S NEXT',
    description: 'Caption for the prediction or what is next slide in the year-end wrapped summary',
  })

  const headingActiveBooking = intl.formatMessage({
    id: 'BYYd0Wi5gYl4qKGkZQVx',
    defaultMessage: 'Your dream {destination} trip is getting closer every day.',
    description: 'Prediction slide heading for active booking in the trip planner',
  }, { destination: activeBooking?.destination ?? '' })

  const headingDaysToGo = intl.formatMessage({
    id: 'AFy2Zl80VaNxSphd6mKH',
    defaultMessage: '{daysToGo} days to go!',
    description: 'Prediction slide days to go heading in the year-end travel wrapped summary',
  }, { daysToGo: activeBooking?.daysToGo ?? 0 })

  const buttonStartPlanning = intl.formatMessage({
    id: 'RU_Tv1jZE4eq_YJtdThG',
    defaultMessage: 'Start planning your escape',
    description: 'Prediction slide button label to start planning in the year-in-review feature',
  })

  const headingTripTo = intl.formatMessage({
    id: 'vQbZx5o4D_H5zcv_romt',
    defaultMessage: 'This year, a trip to',
    description: 'Prediction slide heading introducing destination in the year-in-review feature',
  })

  const headingIsCalling = intl.formatMessage({
    id: 'g3xXZlyADkNikimGCulD',
    defaultMessage: '{destinationName} is calling!',
    description: 'Prediction slide heading with destination name in the year-in-review feature',
  }, { destinationName: destinationName ?? '' })

  const bodyTextManyOffers = intl.formatMessage({
    id: '3Z0TTZofjgjoCNCfc3ml',
    defaultMessage: 'Many amazing offers available',
    description: 'Prediction slide body text for many offers without savings',
  })

  const bodyTextOffersWithSavings = intl.formatMessage({
    id: 'llu9MzeYIz4gg8balKBC',
    defaultMessage: '{offerCount} amazing offers with savings up to {maxSavingsPercent}%.',
    description: 'Prediction slide body text for offers with savings percentage',
  }, {
    offerCount: offerCount || 'Many',
    maxSavingsPercent: predictionData?.maxSavingsPercent ?? 0,
  })

  const bodyTextOffers = offerCount
    ? intl.formatMessage({
        id: 'QTbIJqpPCW1kIcoGnhxq',
        defaultMessage: '{offerCount} amazing offers available',
        description: 'Prediction slide body text for specific number of offers',
      }, { offerCount })
    : bodyTextManyOffers

  const buttonExplore = intl.formatMessage({
    id: 'ueJRcstTpnukrI04ngpY',
    defaultMessage: 'Explore {destinationName}',
    description: 'Prediction slide button label to explore destination',
  }, { destinationName: destinationName ?? '' })

  const headingReadyForTrip = intl.formatMessage({
    id: '7aUtXvZPQiP7SfZdZ7Tz',
    defaultMessage: 'Ready for your next trip?',
    description: 'Prediction slide heading asking if ready for next trip',
  })

  const headingStartPlanning2026 = intl.formatMessage({
    id: 'nargO2n4H6_P1Y65b6FU',
    defaultMessage: 'Start planning your perfect escape for 2026',
    description: 'Prediction slide heading to start planning for 2026',
  })

  const buttonBrowseBestOf2025 = intl.formatMessage({
    id: 'sSfSQ_QB8mEsruPWy7jb',
    defaultMessage: 'Browse Best of 2025',
    description: 'Prediction slide button label to browse Best of 2025',
  })

  // Show active booking variant if user has one
  if (activeBooking) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
              <Heading variant="heading5"><b>{headingActiveBooking}</b></Heading>
            </Group>
            <Heading variant="heading1alt">{headingDaysToGo}</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={`/trip-planner/generate-trip?orderId=${activeBooking.orderId}`}
              >
                {buttonStartPlanning}
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else if (predictionData) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
              <Heading variant="heading5"><b>{headingTripTo}</b></Heading>
            </Group>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading1alt">{headingIsCalling}</Heading>
              <BodyText variant="medium">
                {showSavings ? bodyTextOffersWithSavings : bodyTextOffers}
              </BodyText>
            </Group>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={searchUrl}
              >
                {buttonExplore}
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
              <Heading variant="heading5"><b>{headingReadyForTrip}</b></Heading>
            </Group>
            <Heading variant="heading1alt">{headingStartPlanning2026}</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to="/search?destinationName=Anywhere&destinationId=le_d41d8cd98f00b204e9800998ecf8427e&adults=2&children=none&campaigns=Best+of+2025"
              >
                {buttonBrowseBestOf2025}
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  }
}

export default React.memo(PredictionSlide)
